import type { MultipartUploadPart } from "@/api";
import { api } from "@/api";

export interface UploadProgress {
  file: File;
  progress: number;
  url?: string;
  error?: string;
}

export class OSSUploader {
  async uploadFile(
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<string> {
    try {
      // 1) 初始化分段上传
      const initResp = await api.agent.initMultipartUpload({
        fileName: file.name,
        contentType: file.type,
        fileSize: file.size,
      });
      const initData = initResp.data;

      if (
        !initData ||
        !initData.uploadId ||
        !initData.objectKey ||
        !initData.signedUrls ||
        !initData.partCount
      ) {
        throw new Error("服务端返回的初始化数据不完整");
      }

      const partCount = initData.partCount;
      const signedUrls = initData.signedUrls;
      if (!Array.isArray(signedUrls) || signedUrls.length !== partCount) {
        throw new Error("服务端返回的 signedUrls 数量与 partCount 不一致");
      }

      // 2) 并发上传各个分段（均分文件，最后一段取尾）
      const partSize = Math.max(Math.ceil(file.size / partCount), 1);
      const uploadPromises: Array<Promise<MultipartUploadPart>> = [];
      let completedParts = 0;

      for (let i = 0; i < partCount; i++) {
        const start = i * partSize;
        const end = Math.min(start + partSize, file.size);
        const chunk = file.slice(start, end);

        uploadPromises.push(
          (async () => {
            const resp = await fetch(signedUrls[i]!, {
              method: "PUT",
              body: chunk,
              headers: {
                "Content-Type": "application/octet-stream",
              },
            });
            if (!resp.ok) {
              throw new Error(
                `Failed to upload part ${i + 1}: ${resp.status} ${
                  resp.statusText
                }`
              );
            }
            const etagHeader =
              resp.headers.get("ETag") || resp.headers.get("etag");
            if (!etagHeader) {
              throw new Error(`No ETag received for part ${i + 1}`);
            }
            const cleanEtag = etagHeader.replace(/"/g, "");

            completedParts++;
            if (onProgress) {
              onProgress(Math.floor((completedParts / partCount) * 100));
            }

            return {
              part_number: i + 1,
              etag: cleanEtag,
            } as MultipartUploadPart;
          })()
        );
      }

      const results = await Promise.all(uploadPromises);
      results.sort((a, b) => a.part_number! - b.part_number!);

      // 3) 完成分段上传
      const completeResp = await api.agent.completeMultipartUpload({
        uploadId: initData.uploadId,
        objectKey: initData.objectKey,
        parts: results,
      });

      if (!completeResp.data || !completeResp.data.downloadUrl) {
        throw new Error("完成分段上传失败：服务端未返回下载地址");
      }
      return completeResp.data.downloadUrl;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Multipart upload failed:", err);
      throw new Error(`文件上传失败: ${file.name}`);
    }
  }

  async uploadFiles(
    files: File[],
    onProgress?: (progressList: UploadProgress[]) => void
  ): Promise<string[]> {
    const progressList: UploadProgress[] = files.map((f) => ({
      file: f,
      progress: 0,
    }));
    if (onProgress) onProgress([...progressList]);

    const urls = await Promise.all(
      files.map(async (file, index) => {
        try {
          const url = await this.uploadFile(file, (p) => {
            progressList[index]!.progress = p;
            if (onProgress) onProgress([...progressList]);
          });
          progressList[index]!.url = url;
          return url;
        } catch (e) {
          const msg = e instanceof Error ? e.message : "上传失败";
          progressList[index]!.error = msg;
          if (onProgress) onProgress([...progressList]);
          throw e;
        }
      })
    );

    return urls;
  }

  private formatFileSize(bytes: number): string {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"] as const;
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${Math.round((bytes / Math.pow(k, i)) * 100) / 100} ${sizes[i]}`;
  }
}

export const ossUploader = new OSSUploader();
